import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import BookingModal from "../booking/BookingModal";
import { GetBookingPageBySlugAction } from "../../store/action";
import { usePageMeta } from "../../hooks/usePageMeta";
import { EMBED_LANGUAGES, accentStyleFor, postToHost } from "../../utils/embed";

/**
 * The booking widget: the page `public/embed.js` loads into an iframe on a
 * business's own site, at `/embed/:slug`.
 *
 * It is the public booking form and nothing else — no header, hero or footer,
 * because the site around it is the business's. Everything it books goes
 * through the same anonymous endpoint as the booking page, labelled "Widget" so
 * the business can see what its site brings in.
 *
 * Three things it owes the host page, all by postMessage:
 * - `resize` whenever its height changes, so the iframe fits the form instead
 *   of scrolling inside the host's own scroll;
 * - `close` when the visitor closes the popup;
 * - `booked` once a booking is confirmed — with no personal data, since we
 *   cannot know who is listening (see `postToHost`).
 */
const EmbedBooking = ({ organizationSlug, mode, lang }) => {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const wrapperRef = useRef(null);

  // Crawled through the iframe src on the business's site, this is a second
  // copy of the business's booking page; it names that page as the original.
  usePageMeta({ path: page?.slug ? `/${page.slug}` : null });

  useEffect(() => {
    let cancelled = false;

    GetBookingPageBySlugAction(organizationSlug)
      .then((data) => {
        if (cancelled) return;
        setPage(data);

        // The business's language unless the snippet asked for another: the
        // widget sits on the business's site, among the business's words, and
        // its customers get their emails in that language too. i18n is set to
        // cache nothing on this path (utils/i18n.js), so this never overwrites
        // the language someone chose for our own site in this browser.
        const wanted = lang || data?.locale;
        if (EMBED_LANGUAGES.includes(wanted) && i18n.language !== wanted) {
          i18n.changeLanguage(wanted);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error loading the booking widget:", err);
        setError(
          err.response?.status === 404 ? "Embed.notFound" : "BookingPage.error",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [organizationSlug, lang, i18n]);

  // Report our height to the host. Measured on our own wrapper rather than the
  // document, which is at least as tall as the iframe that holds it and so
  // could only ever grow.
  useEffect(() => {
    const element = wrapperRef.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;

    let last = 0;
    const report = () => {
      const height = Math.ceil(element.getBoundingClientRect().height);
      if (height !== last) {
        last = height;
        postToHost("resize", { height });
      }
    };

    const observer = new ResizeObserver(report);
    observer.observe(element);
    report();
    return () => observer.disconnect();
  }, [page, error]);

  // Escape closes the popup. The host listens for it too, but a key pressed
  // while focus is inside this iframe never reaches the host page.
  useEffect(() => {
    if (mode !== "popup") return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") postToHost("close");
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mode]);

  const accentStyle = useMemo(
    () => accentStyleFor(page?.primaryColor),
    [page?.primaryColor],
  );

  let content;
  if (error) {
    content = <p className="p-6 text-center text-fluid-sm text-ink-600">{t(error)}</p>;
  } else if (!page) {
    content = (
      <p className="p-6 text-center text-fluid-sm text-ink-500">
        {t("BookingPage.loading")}
      </p>
    );
  } else {
    content = (
      <>
        <BookingModal
          isOpen
          embedded={mode}
          organizationSlug={organizationSlug}
          organizationName={page.businessName}
          source="Widget"
          accentStyle={accentStyle}
          onClose={mode === "popup" ? () => postToHost("close") : undefined}
          onBooked={() => postToHost("booked")}
        />
        {page.showsBadge && (
          <p className="pt-3 text-center text-xs">
            {/* A new tab: a link that navigated the iframe would load our
                homepage inside the business's site. */}
            <a
              href={`${window.location.origin}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-ink-500 underline-offset-2 hover:underline"
            >
              {t("Embed.badge")}
            </a>
          </p>
        )}
      </>
    );
  }

  return (
    <div ref={wrapperRef} className="embed-booking">
      {content}
    </div>
  );
};

EmbedBooking.propTypes = {
  organizationSlug: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["popup", "inline"]).isRequired,
  /** Asked for by the snippet (`data-lang`); otherwise the business's own. */
  lang: PropTypes.string,
};

export default EmbedBooking;
